////////////////////////////////////////////////////////////////////////////////
// Impact Theme Extend
// This allows us to change functionality of the default theme
////////////////////////////////////////////////////////////////////////////////

// Import the original classes
import {LineItemQuantity, ProductForm} from "theme";

// Custom LineItemQuantity
class HlcLineItemQuantity extends LineItemQuantity {
  constructor() {
    super();
    this.addEventListener("change", this._onQuantityChanged);
    this.addEventListener("click", this._onIncrementButtonClicked.bind(this));
    this.addEventListener("click", this._onDecrementButtonClicked.bind(this));
  }

  _onIncrementButtonClicked(event) {
    if (event.target.classList.contains("increment-button")) {
      const quantityInput = this.querySelector(".quantity-input");
      const newQty = parseInt(quantityInput.value, 10) + 1;
      this._changeLineItemQuantity(quantityInput.getAttribute("data-line-key"), newQty);
    }
  }

  _onDecrementButtonClicked(event) {
    if (event.target.classList.contains("decrement-button")) {
      const quantityInput = this.querySelector(".quantity-input");
      const newQty = Math.max(0, parseInt(quantityInput.value, 10) - 1);
      this._changeLineItemQuantity(quantityInput.getAttribute("data-line-key"), newQty);
    }
  }

  _toggleButtons(disabled) {
    const buttons = this.querySelectorAll('.increment-button, .decrement-button');
    buttons.forEach(button => {
      if (disabled) {
        button.setAttribute('disabled', '');
      } else {
        button.removeAttribute('disabled');
      }
    });
  }

  _disableButtons() {
    this._toggleButtons(true);
  }

  _enableButtons() {
    this._toggleButtons(false);
  }

  async _changeLineItemQuantity(lineKey, targetQuantity) {
    this._disableButtons();
    const inventoryQuantity = parseInt(this.querySelector(".inventory-quantity")?.value);

    // If backorders aren’t allowed and target exceeds inventory, show error and adjust
    if (HLC.canBackOrder !== true && targetQuantity > inventoryQuantity) {
      this._cartError(`Only ${inventoryQuantity} available.`, 3000);
      const quantityInput = this.querySelector(".quantity-input");
      if (quantityInput) {
        quantityInput.value = inventoryQuantity;
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
      targetQuantity = inventoryQuantity;
    }

    const result = await super._changeLineItemQuantity(lineKey, targetQuantity);
    this._enableButtons();
    return result;
  }

  _cartError(message, duration = 5000) {
    const existingAlert = this.closest(".line-item, tr").querySelector('[role="alert"]');
    if (existingAlert) existingAlert.remove();

    // Construct error markup
    const errorSvg = `<svg width="14" viewBox="0 0 18 18">
      <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path>
      <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
    </svg>`;
    const errorHTML = `<p class="text-with-icon text-error text-xs" role="alert">${errorSvg} ${message}</p>`;

    // Insert error message in a designated container (or prepend to the line item)
    const container = this.closest(".line-item, tr").querySelector(".line-item__info");
    if (container) {
      container.insertAdjacentHTML("beforeend", errorHTML);
    } else {
      this.prepend(document.createRange().createContextualFragment(errorHTML));
    }

    // Remove the error message after the specified duration
    setTimeout(() => {
      const alertEl = this.closest(".line-item, tr").querySelector('[role="alert"]');
      alertEl && alertEl.remove();
    }, duration);
  }
}
if (!window.customElements.get("hlc-line-item-quantity")) {
  window.customElements.define("hlc-line-item-quantity", HlcLineItemQuantity);
}

// Custom Product Zoom Button
var CustomProductZoomButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", this._onButtonClicked);
  }

  _onButtonClicked() {
    this.dispatchEvent(new CustomEvent("lightbox:open", {
      bubbles: true,
      detail: {
        index: 5
      }
    }));
  }
};
if (!window.customElements.get("custom-product-zoom-button")) {
  window.customElements.define("custom-product-zoom-button", CustomProductZoomButton, {extends: "button"});
}

// Product Form with oversell protection and Flare
class HLCProductForm extends ProductForm {
  constructor() {
    super();
  }

  async _onSubmit(event) {
    event.preventDefault();

    const submitButton = this.querySelector('button[type="submit"]');
    submitButton?.setAttribute('aria-busy', 'true');

    try {
      // 1. Ensure variant ID exists
      const variantInput = this.querySelector('input[name="id"]');
      if (!variantInput || !variantInput.value) {
        const errMsg = 'Missing variant identifier.';
        this._dispatchCartError(errMsg);
        flare.reportMessage(errMsg, {});
        // Without a variant ID, we literally cannot add to the cart. Stop here.
        return;
      }
      const variantId = variantInput.value;

      // 2. Get the next incoming date if applicable
      const nextIncomingDate = this.getNextIncomingDate(variantId);

      // 3. Fetch variant data (if this fails, we catch the error below and still proceed)
      let variant = null;
      try {
        variant = await this._fetchVariantData(variantId);
      } catch (fetchError) {
        console.error('❌ Error fetching variant data:', fetchError);
        flare.report(fetchError);
      }

      // If we couldn't fetch or parse variant data, still proceed to add to cart
      // per your requirement ("if anything fails, items are added to cart").
      // But if we have variant data, we can do extra checks.
      if (variant) {
        // Check if the variant can be back-ordered or is a pre-order
        const inventoryPolicy = variant.inventory_policy;
        const customerCanBackOrder = inventoryPolicy === 'continue' && HLC?.canBackOrder === true;
        const variantIsPreOrder = !!(nextIncomingDate && nextIncomingDate.trim() !== '');

        if (customerCanBackOrder || variantIsPreOrder) {
          // If back-order is allowed or it's a pre-order, just add to cart.
          await super._onSubmit(event);
          return;
        }

        // Validate and parse the requested quantity
        const quantityInputEl = this.querySelector('input[name="quantity"]');
        let requestedQty = parseInt(quantityInputEl?.value, 10);
        if (isNaN(requestedQty) || requestedQty < 1) {
          requestedQty = 1;
        }

        // Check available stock
        const availableStock = (typeof variant.inventory_quantity === 'number' && variant.inventory_quantity >= 0)
            ? variant.inventory_quantity
            : 0;

        // If requested qty exceeds available stock, block the cart add.
        // This is intentional logic, not an unexpected error,
        // so we do NOT proceed with cart addition here.
        if (requestedQty > availableStock) {
          const errMsg = `Cannot add to cart: only ${availableStock} available.`;
          this._dispatchCartError(errMsg);
          return;
        }
      }

      // If we get here, either we have no variant data but want to fall back and proceed,
      // or we’ve passed all checks. So, go ahead and add to cart.
      await super._onSubmit(event);

    } catch (error) {
      // If absolutely anything else blows up, log it, report it,
      // but still proceed with adding to cart.
      console.error('❌ Unexpected error in _onSubmit:', error);
      flare.report(error);
      await super._onSubmit(event);
    } finally {
      submitButton?.removeAttribute('aria-busy');
    }
  }

  /**
   * Fetches the variant data from Shopify’s product JSON endpoint
   * given a variant ID.
   */
  async _fetchVariantData(variantId) {
    // Try/catch here so if fetch or JSON parsing fails,
    // we gracefully return null and let the parent _onSubmit decide next steps.
    try {
      const pathName = window.location.pathname.replace(/\/+$/, '');
      const segments = pathName.split('/').filter(Boolean);
      const productHandle = segments.pop() || '';

      if (! productHandle) {
        const errMsg = 'Missing product handle.';
        console.error('❌', errMsg);
        flare.reportMessage(errMsg, { variantId });
        return null;
      }

      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        const errMsg = `Fetch failed: ${response.status} ${response.statusText}`;
        console.error('❌', errMsg);
        flare.reportMessage(errMsg, { productHandle, variantId });
        return null;
      }

      const product = await response.json();
      const variant = product.variants.find(v => String(v.id) === String(variantId));

      if (!variant) {
        const warnMsg = `Variant not found for id: ${variantId}`;
        console.warn('❌', warnMsg);
        flare.reportMessage(warnMsg, { productHandle, variantId });
      }
      return variant || null;
    } catch (error) {
      console.error('❌ Error fetching variant data:', error);
      flare.report(error);
      return null;
    }
  }

  /**
   * Returns the "next incoming date" for a given variant ID.
   * Also updates the global NextIncomingDate input if present.
   */
  getNextIncomingDate(variantId) {
    try {
      const variantInput = document.querySelector(`input[data-variant-id="${variantId}"]`);
      const nextIncomingDate = variantInput?.value?.trim() || null;
      const globalInput = document.getElementById('NextIncomingDate');
      if (globalInput) {
        globalInput.value = nextIncomingDate || '';
        if (!nextIncomingDate) {
          globalInput.setAttribute('disabled', '');
        } else {
          globalInput.removeAttribute('disabled');
        }
      }
      return nextIncomingDate;
    } catch (error) {
      console.error('❌ Error in getNextIncomingDate:', error);
      flare.report(error);
      return null;
    }
  }

  /**
   * Helper to dispatch a cart:error event consistently.
   */
  _dispatchCartError(message) {
    this.dispatchEvent(new CustomEvent('cart:error', {
      bubbles: true,
      detail: { error: message },
    }));
  }
}
if (!window.customElements.get('hlc-product-form')) {
  window.customElements.define('hlc-product-form', HLCProductForm, { extends: 'form' });
}

